//import {unicodeToChar} from "../../utils/strings";

const typeVariable = (key, type, name) => {
  return {name: name || key, type};
};

const variableReplace = (key, match, name) => {
  if (match) {
    return `${key}_${match}: $${name || key}`;
  } else {
    return `${key}: $${name || key}`;
  }
};

const formatParams = (entity, params, vars, queryVariables, options) => {
  const values = [];

  const containers = {
    entity,
    params,
    queryVariables,
    options
  };

  // Array can be used for multiple match operations on the same field,
  // ex: name_like and & name_ne on the same query, or just as a longer
  // form. For simpler queries, a simple map can be used.
  if (Array.isArray(vars)) {
    vars.forEach(v => {
      const value = formatParam(v.field, v, containers);
      if (typeof value !== "undefined" && value !== "") {
        values.push(value);
      }
    });
  } else {
    for (let key in vars) {
      const value = formatParam(key, vars[key], containers);
      if (typeof value !== "undefined" && value !== "") {
        values.push(value);
      }
    }
  }

  return values;
};

const formatParam = (field, variable, {params, queryVariables}) => {
  // Name override is more important since variables in the query can appear
  // multiple times, but names should be unique unless intentionally reused.
  let format = false;
  if (variable.name) {
    format = params.hasOwnProperty(variable.name);
  } else {
    format = params.hasOwnProperty(field);
  }

  // Only process if there is a param value
  if (format) {
    const {name, match} = variable;
    let {type} = variable;

    queryVariables.push(typeVariable(field, type, name));
    return variableReplace(field, match, name);
  }
};

const formatQuery = (
  entity,
  fields = [],
  params = [],
  {limit = 0, sort = {}, pagination = {}} = {}
) => {
  const {field, direction} = sort,
    {after, before} = pagination;

  if (!after && !before && limit) {
    params.push(`first: ${limit}`);
  }

  if (after) {
    params.push(`after: "${after}"`);

    if (limit) {
      params.push(`first: ${limit}`);
      params.push(`before: null`);
      params.push(`last: null`);
    }
  }

  if (before) {
    params.push(`before: "${before}"`);

    if (limit) {
      params.push(`last: ${limit}`);
      params.push(`after: null`);
      params.push(`first: null`);
    }
  }

  if (field && direction) {
    params.push(`orderBy:{field:${field}, direction:${direction}}`);
  }

  let paramString = "",
    fieldString = "";
  if (params.length) {
    paramString = `(${params.join(", ")})`;
  }

  if (fields.length) {
    fieldString = ` {${fields.join(" ")}}`;
  }

  return `${entity}${paramString}${fieldString}`;
};

const format = (
  {entity, fields = [], vars = [], options},
  params,
  queryVariables
) => {
  if (!entity) {
    return;
  }

  const processedFields = [];

  fields.forEach(field => {
    if (field === Object(field)) {
      if (!field.options) {
        field.options = {};
      }

      field.options.schema = options.schema;
      field.options.parent = entity;

      const processed = format(field, params, queryVariables);
      processedFields.push(processed);
    } else {
      processedFields.push(field);
    }
  });

  let processedParams;

  if (params) {
    processedParams = formatParams(
      entity,
      params,
      vars,
      queryVariables,
      options
    );
  }

  return formatQuery(entity, processedFields, processedParams, options);
};

const wildCardWrap = (value, split = false) => {
  if (split) {
    return value.split(",").map(v => {
      return `*${v}*`;
    });
  }

  return `*${value}*`;
};

export default {
  typeVariable,
  variableReplace,
  wildCardWrap,

  /**
  Builds a query from an object with entity, fields, search parameters, and
  match descriptions.
  @param {object} definition - entity string, requested fields array, search
   variable match specifiers.

    Match specifiers:

      vars: {
        organization_id: {match: "eq"},
        active: {match: "is"}
      }

    For more complex queries, likely to happen in advanced search, a more
    explicit format can be used for the variables.

      vars: [
        ///////////////
        {field: "login", match: "like", name: "login_like"},
        {field: "login", match: "ne"},
        ///////////////
        {field: "organization_id", match: "eq"},
        {field: "active", match: "is"}
      ]

    The values in params can then be along the lines of:
      {
        name_like: "foo",
        name: "not_foo"
        organization_id: 1
      }
  */

  query: (definition, params, options = {}) => {
    const queryVariables = [],
      queryName = options.queryName || "adHocQuery";

    const definitionList = Array.isArray(definition)
      ? definition
      : [definition];

    const queries = definitionList.map(def => {
      const formatted = format({...def, options}, params, queryVariables);
      return `${formatted}`;
    });

    let query = `{${queries.join(" ")}}`;

    if (queryVariables.length) {
      // Safety check, only use first instance (two search values have the
      // same key)
      const unique = [];
      queryVariables.forEach(v => {
        let skip = false;
        unique.forEach(u => {
          if (v.name === u.name && v.type !== u.type) {
            // type conflict, error
            console.error(
              `Variable (${v.name}) has multiple conflicting types:`,
              v.type,
              u.type
            );
            skip = true;
          } else if (v.name === u.name && v.type === u.type) {
            // direct match, don't add
            skip = true;
          }
        });

        if (!skip) {
          unique.push(v);
        }
      });

      const variablesString = unique
        .map(v => `$${v.name}: ${v.type}`)
        .join(", ");

      query = `query ${queryName}(${variablesString}) ${query}`;
    }

    return query;
  }
};
